import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { resolveImageUrl } from "@/lib/images";

export default function ClaimListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [email, setEmail] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => {
      setListing(r.data);
      setEmail(r.data.email || (user?.email ?? ""));
    });
  }, [id, user]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/listings/${id}/claim`, {
        claimed_email: email,
        doc_url: docUrl || null,
        message,
      });
      setDone(true);
      toast.success("Claim submitted — we'll review and email you.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!listing) return <div className="container-page py-16">Loading…</div>;
  if (listing.status !== "unclaimed") {
    return (
      <div className="container-page py-16 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">This listing isn't available to claim</h1>
        <p className="mt-2 text-muted-foreground">Status: {listing.status}</p>
        <Link to={`/listing/${id}`} className="btn-secondary mt-6 inline-flex">Back to listing</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="container-page py-16 max-w-2xl text-center" data-testid="claim-success">
        <span className="grid mx-auto h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground">
          <ShieldCheck className="h-7 w-7" />
        </span>
        <h1 className="mt-6 font-display text-3xl font-bold text-primary">Claim received</h1>
        <p className="mt-3 text-foreground/70">
          A manager will review your documents and respond by email. Once approved, this listing will appear in your dashboard.
        </p>
        <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8">Go to dashboard</button>
      </div>
    );
  }

  return (
    <div className="container-page py-12" data-testid="claim-page">
      <Link to={`/listing/${id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back to listing
      </Link>
      <div className="mt-8 max-w-2xl">
        <span className="chip">Claim listing</span>
        <h1 className="mt-3 font-display text-4xl font-bold text-primary">{listing.practice_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{listing.category} {listing.city ? `· ${listing.city}` : ""}</p>

        <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="claim-form">
          <label>
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Confirm contact email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
              data-testid="claim-email"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Use an email associated with this practice (it helps us verify quickly).
            </p>
          </label>

          <div>
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Verification document</span>
            <div className="flex items-center gap-4">
              {docUrl ? (
                <a href={resolveImageUrl(docUrl)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-primary hover:text-accent">View uploaded</a>
              ) : (
                <span className="text-sm text-muted-foreground">e.g. medical license, business card, organization letterhead</span>
              )}
              <ImageUpload label={docUrl ? "Replace" : "Upload"} testId="claim-doc" onUploaded={(url) => setDocUrl(url)} />
            </div>
          </div>

          <label>
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-foreground/70">Note to reviewer (optional)</span>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-md border border-border bg-card px-3 py-2.5 outline-none focus:ring-2 focus:ring-accent"
              data-testid="claim-message"
              placeholder="Anything else we should know?"
            />
          </label>

          <button type="submit" disabled={submitting} className="btn-primary w-fit" data-testid="claim-submit">
            {submitting ? "Submitting…" : "Submit claim"}
          </button>
        </form>
      </div>
    </div>
  );
}
